function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { call, select, getContext } from 'redux-saga/effects';
import OidcService from 'services/OidcService';
import { ApiError, LocalError } from 'helpers/errorTypes';
import { regionName as regionNameSelector, originalRegionName as originalRegionNameSelector, regions as regionsSelector } from 'modules/App/selectors';
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */ function parseJSON(response, options) {
    let json;
    try {
        json = response.text().then((text)=>{
            try {
                if (!text) return null;
                return JSON.parse(text);
            } catch (err) {
                throw new ApiError({
                    url: get(response, 'url'),
                    status: response.status,
                    error: err,
                    validationErrors: {},
                    options: omit(pick(options, [
                        'method',
                        'headers'
                    ]), 'headers.Authorization')
                });
            }
        });
    } catch (err) {
        throw new ApiError({
            url: get(response, 'url'),
            status: 500,
            error: err,
            validationErrors: {},
            options: omit(pick(options, [
                'method',
                'headers'
            ]), 'headers.Authorization')
        });
    }
    if (response.status >= 200 && response.status < 300) {
        return json;
    }
    return json.then((resp)=>{
        throw new ApiError({
            url: get(response, 'url'),
            status: get(response, 'status'),
            validationErrors: get(resp, 'validationErrors'),
            businessError: get(resp, 'businessError') || get(resp, 'message'),
            requestId: get(resp, 'requestId'),
            options: omit(pick(options, [
                'method',
                'headers'
            ]), 'headers.Authorization')
        });
    });
}
function* request(url, options, regionName, fetch) {
    const token = yield call(OidcService.getToken);
    if (!fetch) {
        fetch = yield getContext('fetch');
    }
    options = _object_spread_props(_object_spread({}, options), {
        headers: _object_spread({}, token ? {
            Authorization: `Bearer ${token}`
        } : null, get(options, 'headers', {}))
    });
    if (url.startsWith('/api')) {
        url = url.replace('/api', `/api/${regionName}`);
    }
    let response;
    try {
        response = yield call(fetch, url, options);
    } catch (err) {
        throw new ApiError({
            url,
            status: 503,
            error: `${err}`,
            validationErrors: {},
            options: omit(pick(options, [
                'method',
                'headers'
            ]), 'headers.Authorization')
        });
    }
    return yield call(parseJSON, response, options);
}
function* originalRequest(url, options, fetch = null) {
    const regionName = yield select(originalRegionNameSelector);
    return yield call(request, url, options, regionName, fetch);
}
function* regionalRequest(url, options, fetch = null) {
    const regionName = yield select(regionNameSelector);
    return yield call(request, url, options, regionName, fetch);
}
function* crossRegionalRequest(region, url, options, fetch = null) {
    return yield call(request, url, options, region.name, fetch);
}
function* findRegionByCountry(alpha2Code) {
    if (!alpha2Code) {
        return null;
    }
    const regions = yield select(regionsSelector);
    const region = find(regions, (r)=>includes(r.countries, alpha2Code));
    if (!region) {
        throw new LocalError({
            code: 'InvalidRegion'
        });
    }
    return region;
}
// HELPERS -------------------------------------------------------------------------------------------------------------
function* findRegions(objectArray, countryCodeKey = 'countryCode') {
    const regions = yield select(regionsSelector);
    return objectArray.reduce((acc, object)=>{
        if (!object) return acc;
        const region = find(regions, (r)=>includes(r.countries, object[countryCodeKey]));
        if (region) acc.add(region);
        return acc;
    }, new Set());
}
export default {
    originalRequest,
    regionalRequest,
    crossRegionalRequest,
    findRegionByCountry,
    findRegions
};
